import type React from 'react'
import { createContext, useContext } from 'react'
import { LoadingElement } from './loading.tsx'
import { useFlightDeckSession } from './use-flight-deck-session.ts'

export type AppSession = {
  advertiser: {
    id: number
    name: string
    logo: string | undefined
  }
}

export type FlightDeckSession = AppSession & {
  loginAs: 'admin' | 'demo' | 'advertiser'
  cloudinaryAccessToken: string
  cloudinaryAccessTokenExpiresAt: number
}

type BaseSessionContext = {
  isLoaded: boolean
  getToken: () => Promise<string | null>
}

type UnauthenticatedSessionContext = BaseSessionContext & {
  isSignedIn: false
  session: null
}

type AuthenticatedSessionContext = BaseSessionContext & {
  isSignedIn: true
  session: FlightDeckSession
}

type SessionContextType = UnauthenticatedSessionContext | AuthenticatedSessionContext

const SessionContext = createContext<SessionContextType | undefined>(undefined)

/**
 * Loads the current session from the flight-deck `/v1/me` endpoint.
 *
 * The `session`, `isLoaded`, and `isSignedIn` props are ignored — kept for
 * backwards compatibility with the Premier UI integration contract. They will
 * be removed in a follow-up once Premier stops passing them.
 */
export const SessionProvider = ({
  children,
  getToken,
}: {
  children: React.ReactNode
  /** @deprecated ignored; session is loaded from `/v1/me` */
  session?: AppSession
  /** @deprecated ignored; loaded state is derived from the `/v1/me` query */
  isLoaded?: boolean
  /** @deprecated ignored; signed-in state is derived from the `/v1/me` query */
  isSignedIn?: boolean
  getToken: () => Promise<string | null>
}) => {
  const { session, isLoading } = useFlightDeckSession({
    getToken,
    enabled: true,
  })

  if (isLoading) {
    return <LoadingElement />
  }

  if (!session) {
    return null
  }

  const authValue: AuthenticatedSessionContext = {
    isLoaded: true,
    isSignedIn: true,
    session,
    getToken,
  }

  return <SessionContext.Provider value={authValue}>{children}</SessionContext.Provider>
}

/**
 * useSession:
 * - Allows access to authentication context in both signed-in and signed-out states.
 * - Useful when your component must handle logic for a potentially logged-in or logged-out user.
 */
export const useSession = () => {
  const sessionContext = useContext(SessionContext)
  if (!sessionContext) {
    throw new Error('Authorization unavailable')
  }
  return sessionContext
}

/**
 * useAuthenticatedSession:
 * - For use from within an authenticated route where the user authentication is gauranteed.
 * - Contains additional convenience fields and ability to skip null checks on the sesssion.
 */
export const useAuthenticatedSession = () => {
  const authenticatedSessionContext = useSession()

  if (!authenticatedSessionContext.isSignedIn) {
    throw new Error(
      'useAuthenticatedSession was called but there is no user session. Did you forget to protect this route?',
    )
  }

  return authenticatedSessionContext
}
